import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, App } from 'antd';
import { NumberOutlined, ReloadOutlined, MailOutlined, LockOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import Auth from '../../../../service/Auth';
import { useNotify, useWithNotify } from '../../../../providers/NotificationProvider';

const ConfirmAccount = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email || '';
    const savedPassword = location.state?.savedPassword || '';
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    
    const notify = useNotify();
    const withNotify = useWithNotify();
    const { modal } = App.useApp(); // ⭐ Usar modal de App

  

    useEffect(() => {
        if (!email) {
            notify.warning('Falta email. Vuelve a registrarte para confirmar la cuenta.');
        }
    }, [email, notify]);

    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => setCooldown((c) => c - 1), 1000);
        }
        return () => timer && clearInterval(timer);
    }, [cooldown]);

    const canResend = useMemo(() => cooldown === 0, [cooldown]);

    const onFinish = async (values) => {
        const { code, password } = values;
        setLoading(true);

        try {
            // Usar la contraseña del formulario O la guardada
            const passwordToUse = password || savedPassword;

            if (!passwordToUse) {
                notify.error('Debes ingresar tu contraseña para confirmar la cuenta');
                setLoading(false);
                return;
            }



            // Enviar código Y contraseña
            const confirmResponse = await withNotify(
                Auth.confirmAccount(email, code, passwordToUse),
                {
                    loading: 'Confirmando cuenta...',
                    success: 'Cuenta confirmada exitosamente',
                    error: 'Error al confirmar la cuenta'
                }
            );

          

            if (confirmResponse) {
                // Iniciar sesión automáticamente
                try {
                    await signIn({ username: email, password: passwordToUse });
                    notify.success('Sesión iniciada automáticamente');
                    navigate('/perfil');
                    window.location.reload();
                } catch (signInError) {
                    console.error('Error al iniciar sesión:', signInError);
                    notify.warning('Cuenta confirmada. Por favor inicia sesión manualmente.');
                    navigate('/login');
                }
            }
        } catch (err) {
            console.error('Error confirmando cuenta:', err);
            notify.error(err?.error || err?.message || 'Código o contraseña incorrectos. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;
        
        try {
            setCooldown(60);
            
            await withNotify(
                Auth.resendConfirmationCode(email, 'signup'),
                {
                    loading: 'Reenviando código...',
                    success: 'Código reenviado. Revisa tu correo.',
                    error: 'No se pudo reenviar el código'
                }
            );
        } catch (err) {
            console.error('Error reenviando código:', err);
            setCooldown(0);
            notify.error(err?.message || 'No se pudo reenviar el código. Intenta nuevamente.');
        }
    };

    // ⭐ ACTUALIZADO: Manejador para olvidé mi contraseña usando App.useApp()
    const handleForgotPassword = () => {
        modal.confirm({
            title: '¿Olvidaste tu contraseña?',
            icon: <QuestionCircleOutlined />,
            content: (
                <div>
                    <p className="mb-3">Tienes dos opciones:</p>
                    <div className="space-y-2">
                        <p><strong>Opción 1:</strong> Si recuerdas tu contraseña, ingrésala en el campo de abajo junto con el código.</p>
                        <p><strong>Opción 2:</strong> Si no la recuerdas, puedes ir a "Recuperar Contraseña" para establecer una nueva.</p>
                    </div>
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-800">
                            ⚠️ <strong>Importante:</strong> Si recuperas tu contraseña, deberás volver aquí con el código de confirmación que te enviamos originalmente.
                        </p>
                    </div>
                </div>
            ),
            okText: 'Ir a Recuperar Contraseña',
            cancelText: 'Cerrar',
            width: 500,
            onOk: () => {
                // Redirigir a recuperar contraseña con información de contexto
                navigate('/olvidar-contrasena', { 
                    state: { 
                        email: email,
                        returnTo: '/confirmar-cuenta',
                        needsConfirmation: true 
                    } 
                });
            }
        });
    };

    return (
        <div className="w-full flex items-center justify-center">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
                    <div className="text-center mb-6">
                        <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                            <MailOutlined className="text-blue-600 text-2xl" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">Confirma tu cuenta</h1>
                        <p className="text-gray-600 mt-2">
                            Ingresa el código enviado a <span className="font-semibold">{email || 'tu correo'}</span>
                        </p>
                    </div>

                    <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
                        {/* Código de confirmación */}
                        <Form.Item
                            name="code"
                            label={<span className="font-semibold text-gray-700">Código de confirmación</span>}
                            rules={[
                                { required: true, message: 'Ingresa el código' },
                                { len: 6, message: 'El código debe tener 6 dígitos' },
                            ]}
                            hasFeedback
                        >
                            <Input
                                size="large"
                                prefix={<NumberOutlined className="text-gray-400" />}
                                placeholder="Ej: 123456"
                                inputMode="numeric"
                                maxLength={6}
                            />
                        </Form.Item>

                        {/* Campo de contraseña (solo si NO viene del registro) */}
                        {!savedPassword && (
                            <Form.Item
                                name="password"
                                label={
                                    <div className="flex items-center justify-between w-full">
                                        <span className="font-semibold text-gray-700">Contraseña de registro</span>
                                        <Button 
                                            type="link" 
                                            size="small" 
                                            onClick={handleForgotPassword}
                                            className="p-0 h-auto text-blue-600 hover:text-blue-700"
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </Button>
                                    </div>
                                }
                                rules={[
                                    { required: true, message: 'Ingresa tu contraseña' },
                                    { min: 8, message: 'Mínimo 8 caracteres' }
                                ]}
                                hasFeedback
                                extra={
                                    <div className="mt-2 text-xs text-gray-500">
                                        Ingresa la contraseña que usaste al registrarte
                                    </div>
                                }
                            >
                                <Input.Password
                                    size="large"
                                    prefix={<LockOutlined className="text-gray-400" />}
                                    placeholder="Tu contraseña de registro"
                                />
                            </Form.Item>
                        )}

                        {/* Mensaje informativo si tiene contraseña guardada */}
                        {savedPassword && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    ✅ Tu contraseña está guardada. Solo ingresa el código para confirmar.
                                </p>
                            </div>
                        )}

                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading} 
                            size="large" 
                            className="w-full bg-primary hover:bg-primary/90 font-semibold h-12 rounded-lg"
                        >
                            Confirmar y continuar
                        </Button>

                        <div className="flex items-center justify-between mt-4">
                            <Button 
                                type="default" 
                                size="middle" 
                                onClick={() => navigate('/registro')}
                            >
                                Volver al registro
                            </Button>
                            <Button
                                type="link"
                                icon={<ReloadOutlined />}
                                onClick={handleResend}
                                disabled={!canResend || !email}
                            >
                                {canResend ? 'Reenviar código' : `Reenviar en ${cooldown}s`}
                            </Button>
                        </div>
                    </Form>

                    {/* Nota de ayuda */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600 text-center">
                            💡 <strong>Nota:</strong> Debes ingresar el código de 6 dígitos enviado a tu email 
                            {!savedPassword && ' y tu contraseña de registro'} para activar tu cuenta.
                        </p>
                        {!savedPassword && (
                            <p className="text-xs text-orange-600 text-center mt-2">
                                ⚠️ Si olvidaste tu contraseña, usa el enlace "¿Olvidaste tu contraseña?" arriba.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmAccount;
